import { useState } from "react";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { requestMagicLink } from "../lib/api";

/**
 * EmailSignInDialog — passwordless sign-in via Resend magic link.
 *
 * Renders a "Sign in with email" button. On click, opens a modal that
 * captures the teacher's email and POSTs to /api/auth/email/request.
 * Backend emails them a one-click sign-in URL.
 */
export const EmailSignInDialog = ({
  triggerClassName,
  triggerLabel = "Sign in with email",
  onBeforeOpen,
  // Controlled-from-parent mode — used by the mobile hamburger menu in
  // UserMenu.jsx, which has no room to render the trigger button itself
  // and just wants to flip the dialog open. When `mobileOpen` is non-null
  // we skip rendering DialogTrigger and let the parent drive open/close.
  mobileOpen,
  onMobileOpenChange,
}) => {
  const [localOpen, setLocalOpen] = useState(false);
  const isControlled = typeof onMobileOpenChange === "function";
  const open = isControlled ? !!mobileOpen : localOpen;
  const setOpen = isControlled ? onMobileOpenChange : setLocalOpen;
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      await requestMagicLink(trimmed);
      setSent(true);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Could not send sign-in email";
      toast.error(String(detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (v && typeof onBeforeOpen === "function") {
          try {
            onBeforeOpen();
          } catch {
            /* never block opening on a caller-side stash failure */
          }
        }
        setOpen(v);
        if (!v) {
          setSent(false);
          setEmail("");
        }
      }}
    >
      {!isControlled && (
        <DialogTrigger asChild>
          <Button
            data-testid="sign-in-with-email"
            variant="outline"
            className={
              triggerClassName ||
              "h-9 px-3.5 rounded-lg text-[13px] font-semibold inline-flex items-center gap-1.5 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
            }
          >
            <Mail className="h-4 w-4" /> {triggerLabel}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent
        data-testid="email-signin-dialog"
        className="sm:max-w-md bg-white border-[#4a6984]/15"
      >
        {sent ? (
          <div data-testid="email-signin-sent" className="text-center py-2">
            <div className="mx-auto h-12 w-12 rounded-full bg-[#96ded1]/30 grid place-items-center">
              <CheckCircle2 className="h-6 w-6 text-[#1c2d3d]" />
            </div>
            <h2 className="mt-5 font-extrabold text-xl text-[#1c2d3d]">Check your inbox</h2>
            <p className="mt-3 text-[#4a6984] font-medium leading-relaxed">
              We sent a sign-in link to{" "}
              <span className="font-bold text-[#1c2d3d]">{email}</span>.
              <br />
              Click the link in the email to finish signing in. The link is good for 15 minutes.
            </p>
            <Button
              data-testid="email-signin-close"
              onClick={() => setOpen(false)}
              className="ae-cta h-10 px-5 mt-6 rounded-lg text-sm font-bold"
            >
              Got it
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle
                data-testid="email-signin-title"
                className="font-black text-2xl text-[#1c2d3d] tracking-tight"
              >
                Sign in with email
              </DialogTitle>
              <DialogDescription className="text-[#4a6984] font-medium leading-relaxed">
                No password, no account creation forms. We&apos;ll email you a one-click sign-in link.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-2 space-y-4">
              <div>
                <Label
                  htmlFor="email-signin-input"
                  className="font-bold uppercase text-xs tracking-[0.18em] text-[#4a6984]/70"
                >
                  Email address
                </Label>
                <Input
                  id="email-signin-input"
                  data-testid="email-signin-input"
                  type="email"
                  autoComplete="email"
                  placeholder="you@school.ca"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-12 bg-[#f4f5f7] border-[#4a6984]/20 text-[#4a6984] font-medium focus-visible:ring-[#96ded1]"
                  autoFocus
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="email-signin-submit"
                disabled={busy}
                className="ae-cta h-11 w-full rounded-lg text-sm font-bold inline-flex items-center justify-center gap-2"
              >
                {busy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending link…
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4" /> Email me a sign-in link
                  </>
                )}
              </Button>
              <p className="text-xs text-[#4a6984]/70 font-medium text-center">
                First time signing in? Your account is created automatically.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailSignInDialog;
